import type { ZudokuConfig } from 'zudoku';

const config: ZudokuConfig = {
  site: {
    logo: {
      src: { light: '/logo.svg', dark: '/logo.svg' },
      alt: 'Planbok',
      width: '130px',
    },
    title: 'Planbok API Documentation',
    banner: {
      message:
        'Welcome to the Planbok Developer Documentation! Here, you’ll learn how to leverage our wallet infrastructure to build amazing experiences for your customers.',
      color: '#10b981',
      dismissible: true,
    },
  },
  metadata: {
    title: 'Planbok API Documentation',
    description:
      'Planbok is a programmable wallet infrastructure with built-in payment rails for stablecoin and fiat currencies designed to help businesses easily integrate blockchain and traditional finance based features in treasury management, payment & payroll.',
    logo: '/logo.svg',
    favicon: '/favicon.ico',
    applicationName: 'Planbok',
    referrer: 'no-referrer',
    keywords: [
      'Planbok',
      'Wallet',
      'MPC',
      'API',
      'Web3',
      'Crypto',
      'Payments',
      'Payroll',
      'Treasury Management',
      'Custody',
      'Programmable Wallet',
      'Blockchain',
      'Wallets',
      'Stablecoins',
      'P2P Exchange',
      'Planbok API Documentation',
    ],
    authors: ['Welechi Ifeanyichukwu'],
    creator: 'Welechi Ifeanyichukwu',
    publisher: 'Welechi Ifeanyichukwu',
  },
  theme: {
    light: {
      background: '#ffffff',
      foreground: '#020817',
      card: '#ffffff',
      cardForeground: '#020817',
      popover: '#ffffff',
      popoverForeground: '#020817',
      primary: '#10b981',
      primaryForeground: '#ffffff',
      secondary: '#f1f5f9',
      secondaryForeground: '#020817',
      muted: '#f1f5f9',
      mutedForeground: '#64748b',
      accent: '#d1fae5',
      accentForeground: '#020817',
      destructive: '#ef4444',
      destructiveForeground: '#ffffff',
      border: '#e2e8f0',
      input: '#e2e8f0',
      ring: '#10b981',
      radius: '0.5rem',
    },
    dark: {
      background: '#020817',
      foreground: '#f8fafc',
      card: '#020817',
      cardForeground: '#f8fafc',
      popover: '#020817',
      popoverForeground: '#f8fafc',
      primary: '#10b981',
      primaryForeground: '#f8fafc',
      secondary: '#1e293b',
      secondaryForeground: '#f8fafc',
      muted: '#1e293b',
      mutedForeground: '#94a3b8',
      accent: '#022c22',
      accentForeground: '#f8fafc',
      destructive: '#ef4444',
      destructiveForeground: '#f8fafc',
      border: '#1e293b',
      input: '#1e293b',
      ring: '#10b981',
      radius: '0.5rem',
    },
    customCss: `
      img[src*="#light-mode-only"] {
        display: block !important;
      }
      .dark img[src*="#light-mode-only"] {
        display: none !important;
      }

      img[src*="#dark-mode-only"] {
        display: none !important;
      }
      .dark img[src*="#dark-mode-only"] {
        display: block !important;
      }
    `,
  },
  navigation: [
    {
      type: 'category',
      label: 'Get Started',
      items: [
        {
          type: 'doc',
          file: 'introduction',
          label: 'Introduction',
        },
        {
          type: 'category',
          label: 'Products',
          items: [
            {
              type: 'category',
              label: 'Customer Custody Wallets',
              items: [
                {
                  type: 'doc',
                  file: 'products/customer-custody-wallets/overview',
                  label: 'Overview',
                },
                {
                  type: 'category',
                  label: 'Quickstarts',
                  items: [
                    {
                      type: 'doc',
                      file: 'products/customer-custody-wallets/quickstarts/introduction',
                      label: 'Introduction',
                    },
                    {
                      type: 'doc',
                      file: 'products/customer-custody-wallets/quickstarts/onboarding-and-pin-setup',
                      label: 'Onboarding & PIN Setup',
                    },
                    {
                      type: 'doc',
                      file: 'products/customer-custody-wallets/quickstarts/provisioning-wallets',
                      label: 'Provisioning Wallets',
                    },
                    {
                      type: 'doc',
                      file: 'products/customer-custody-wallets/quickstarts/authorized-transactions',
                      label: 'Authorized Transactions',
                    },
                    {
                      type: 'doc',
                      file: 'products/customer-custody-wallets/quickstarts/signing-messages',
                      label: 'Signing Messages',
                    },
                    {
                      type: 'doc',
                      file: 'products/customer-custody-wallets/quickstarts/security-and-recovery',
                      label: 'Security & Recovery',
                    },
                  ],
                },
                // {
                //   type: 'category',
                //   label: 'Tutorials',
                //   items: [],
                // },
                // {
                //   type: 'category',
                //   label: 'References',
                //   items: [],
                // },
              ],
            },
            {
              type: 'category',
              label: 'Organizational Wallets',
              items: [
                {
                  type: 'doc',
                  file: 'products/organizational-wallets/overview',
                  label: 'Overview',
                },
                {
                  type: 'category',
                  label: 'Quickstarts',
                  items: [
                    {
                      type: 'doc',
                      file: 'products/organizational-wallets/quickstarts/organization-secret-registration',
                      label: 'Organization Secret Registration',
                    },
                    {
                      type: 'doc',
                      file: 'products/organizational-wallets/quickstarts/create-wallet-set',
                      label: 'Create Wallet Set',
                    },
                    {
                      type: 'doc',
                      file: 'products/organizational-wallets/quickstarts/create-wallets',
                      label: 'Create Wallets',
                    },
                    {
                      type: 'doc',
                      file: 'products/organizational-wallets/quickstarts/export-wallets',
                      label: 'Export Wallets',
                    },
                    {
                      type: 'doc',
                      file: 'products/organizational-wallets/quickstarts/performing-transfers',
                      label: 'Performing Token Transfers',
                    },
                    {
                      type: 'doc',
                      file: 'products/organizational-wallets/quickstarts/smart-contract-interactions',
                      label: 'Smart Contract Interactions',
                    },
                    {
                      type: 'doc',
                      file: 'products/organizational-wallets/quickstarts/signing-messages',
                      label: 'Signing Messages',
                    },
                    {
                      type: 'doc',
                      file: 'products/organizational-wallets/quickstarts/managing-transactions',
                      label: 'Managing Transactions',
                    },
                    {
                      type: 'doc',
                      file: 'products/organizational-wallets/quickstarts/viewing-assets',
                      label: 'Viewing Assets & Activity',
                    },
                  ],
                },
                // {
                //   type: 'category',
                //   label: 'Tutorials',
                //   items: [],
                // },
                // {
                //   type: 'category',
                //   label: 'References',
                //   items: [],
                // },
              ],
            },
            {
              type: 'category',
              label: 'Virtual Accounts',
              items: [
                {
                  type: 'doc',
                  file: 'products/virtual-accounts/overview',
                  label: 'Overview',
                },
                {
                  type: 'doc',
                  file: 'products/virtual-accounts/quickstart',
                  label: 'Quickstart',
                },
              ],
            },
          ],
        },
        {
          type: 'category',
          label: 'Concepts',
          items: [
            {
              type: 'doc',
              file: 'concepts/mpc-infrastructure',
              label: 'MPC Infrastructure',
            },
            {
              type: 'doc',
              file: 'concepts/wallet-management',
              label: 'Wallet Management',
            },
            {
              type: 'doc',
              file: 'concepts/custody-models',
              label: 'Custody Models',
            },
            {
              type: 'doc',
              file: 'concepts/wallet-export',
              label: 'Wallet Export & Portability',
            },
            {
              type: 'doc',
              file: 'concepts/security-model',
              label: 'Security Model',
            },
            {
              type: 'doc',
              file: 'concepts/cryptography',
              label: 'Cryptography & Chain Types',
            },
            {
              type: 'doc',
              file: 'concepts/transaction-lifecycle',
              label: 'Transaction Lifecycle',
            },
            {
              type: 'doc',
              file: 'concepts/audit-compliance',
              label: 'Audit & Compliance',
            },
            {
              type: 'doc',
              file: 'concepts/webhooks',
              label: 'Webhooks & Events',
            },
          ],
        },
        // {
        //   type: 'category',
        //   label: 'Quickstarts',
        //   items: [],
        // },
        // {
        //   type: 'category',
        //   label: 'Tutorials',
        //   items: [],
        // },
        {
          type: 'category',
          label: 'References',
          items: [
            {
              type: 'doc',
              file: 'references/choosing-custody-model',
              label: 'Choosing a Custody Model',
            },
            {
              type: 'doc',
              file: 'references/supported-blockchains',
              label: 'Supported Blockchains',
            },
            {
              type: 'doc',
              file: 'references/signing-apis',
              label: 'Signing APIs',
            },
            {
              type: 'doc',
              file: 'references/api-rate-limits',
              label: 'API Rate Limits',
            },
          ],
        },
      ],
    },
    {
      type: 'category',
      label: 'API Reference',
      items: [
        {
          type: 'link',
          label: 'MPC API',
          to: '/api',
        },
        {
          type: 'link',
          label: 'Virtual Accounts API',
          to: '/api/virtual-accounts',
        },
      ],
    },
    {
      type: 'category',
      label: 'Security',
      items: [
        {
          type: 'doc',
          file: 'security/overview',
          label: 'Overview',
        },
        {
          type: 'doc',
          file: 'security/mpc-protocol',
          label: 'MPC Protocol',
        },
        {
          type: 'doc',
          file: 'security/key-management',
          label: 'Key Management',
        },
        {
          type: 'doc',
          file: 'security/platform-security',
          label: 'Platform Security',
        },
        {
          type: 'doc',
          file: 'security/compliance',
          label: 'Audit & Compliance',
        },
        {
          type: 'doc',
          file: 'security/report-vulnerability',
          label: 'Report a Vulnerability',
        },
      ],
    },
  ],
  redirects: [{ from: '/', to: '/introduction' }],
  apis: [
    {
      type: 'file',
      input: './apis/mpc-api.yaml',
      path: '/api',
    },
  ],
  navigationRules: [
    {
      type: 'remove',
      match: 'Schemas',
    },
  ],
  // apiKeys: {
  //   enabled: true,
  // },
};

export default config;
